import { useCallback, useEffect, useRef } from "react";
import { ANIMALS, BUILDINGS } from "../content/world";
import { CROP_BY_ID } from "../content/items";
import { SETTLEMENTS } from "../content/core";
import { currentNpcSchedule, npcsAtLocation } from "../game/engine";
import type { GameState, SettlementId, ToolMode } from "../game/types";
import { useTouchCapability, VirtualJoystick } from "./VirtualJoystick";
import { drawPlayerWorld } from "../render/characterSprites";
import { drawNpcWorld } from "../render/npcSprites";
import { drawAnimalWorld, drawPetWorld } from "../render/petSprites";
import { drawBuildingExterior, drawCropSprite, drawSettlementGround, drawTree } from "../render/worldSprites";

const TILE = 36;
const MAP_W = 42;
const MAP_H = 30;
type TownBuilding = { x: number; y: number; width: number; height: number; index: number };

const TOWN_LAYOUTS: Record<Exclude<SettlementId, "lantern-acre">, TownBuilding[]> = {
  fernhollow: [
    { x: 3, y: 4, width: 5, height: 4, index: 0 }, { x: 12, y: 3, width: 4, height: 3, index: 1 }, { x: 20, y: 5, width: 5, height: 4, index: 2 },
    { x: 31, y: 3, width: 4, height: 3, index: 3 }, { x: 5, y: 22, width: 4, height: 3, index: 4 }, { x: 14, y: 24, width: 5, height: 3, index: 5 },
    { x: 24, y: 22, width: 4, height: 4, index: 6 }, { x: 33, y: 24, width: 5, height: 3, index: 7 }, { x: 2, y: 12, width: 4, height: 3, index: 8 }, { x: 35, y: 12, width: 4, height: 3, index: 9 },
  ],
  ironvale: [
    { x: 2, y: 3, width: 5, height: 4, index: 0 }, { x: 10, y: 5, width: 5, height: 3, index: 1 }, { x: 18, y: 2, width: 6, height: 4, index: 2 },
    { x: 28, y: 5, width: 5, height: 3, index: 3 }, { x: 35, y: 2, width: 5, height: 4, index: 4 }, { x: 3, y: 23, width: 6, height: 4, index: 5 },
    { x: 13, y: 21, width: 5, height: 3, index: 6 }, { x: 22, y: 24, width: 6, height: 3, index: 7 }, { x: 32, y: 21, width: 5, height: 4, index: 8 }, { x: 18, y: 11, width: 6, height: 4, index: 9 },
  ],
  tidecross: [
    { x: 2, y: 8, width: 5, height: 3, index: 0 }, { x: 10, y: 9, width: 4, height: 3, index: 1 }, { x: 17, y: 7, width: 5, height: 4, index: 2 },
    { x: 26, y: 9, width: 4, height: 3, index: 3 }, { x: 34, y: 7, width: 5, height: 4, index: 4 }, { x: 3, y: 22, width: 5, height: 3, index: 5 },
    { x: 12, y: 23, width: 4, height: 3, index: 6 }, { x: 20, y: 21, width: 5, height: 4, index: 7 }, { x: 29, y: 23, width: 4, height: 3, index: 8 }, { x: 36, y: 21, width: 4, height: 4, index: 9 },
  ],
  emberport: [
    { x: 1, y: 3, width: 6, height: 5, index: 0 }, { x: 8, y: 2, width: 5, height: 4, index: 1 }, { x: 15, y: 4, width: 6, height: 4, index: 2 },
    { x: 23, y: 2, width: 5, height: 5, index: 3 }, { x: 31, y: 4, width: 6, height: 4, index: 4 }, { x: 2, y: 22, width: 6, height: 5, index: 5 },
    { x: 10, y: 24, width: 5, height: 3, index: 6 }, { x: 18, y: 21, width: 6, height: 5, index: 7 }, { x: 27, y: 24, width: 5, height: 3, index: 8 }, { x: 34, y: 21, width: 6, height: 5, index: 9 },
  ],
  glassmere: [
    { x: 2, y: 3, width: 4, height: 6, index: 0 }, { x: 9, y: 5, width: 5, height: 4, index: 1 }, { x: 17, y: 2, width: 4, height: 7, index: 2 },
    { x: 25, y: 5, width: 5, height: 4, index: 3 }, { x: 34, y: 3, width: 4, height: 6, index: 4 }, { x: 3, y: 22, width: 5, height: 5, index: 5 },
    { x: 12, y: 23, width: 4, height: 4, index: 6 }, { x: 20, y: 21, width: 5, height: 6, index: 7 }, { x: 29, y: 23, width: 4, height: 4, index: 8 }, { x: 36, y: 21, width: 4, height: 6, index: 9 },
  ],
  auricourt: [
    { x: 2, y: 3, width: 6, height: 5, index: 0 }, { x: 11, y: 3, width: 5, height: 5, index: 1 }, { x: 18, y: 2, width: 6, height: 6, index: 2 },
    { x: 27, y: 3, width: 5, height: 5, index: 3 }, { x: 34, y: 3, width: 6, height: 5, index: 4 }, { x: 2, y: 22, width: 6, height: 5, index: 5 },
    { x: 11, y: 22, width: 5, height: 5, index: 6 }, { x: 18, y: 21, width: 6, height: 6, index: 7 }, { x: 27, y: 22, width: 5, height: 5, index: 8 }, { x: 34, y: 22, width: 6, height: 5, index: 9 },
  ],
};

function townBuildings(location: SettlementId): TownBuilding[] {
  if (location === "lantern-acre") return [];
  return TOWN_LAYOUTS[location];
}

type Interaction =
  | { kind: "building"; uid: string; label: string; distance: number }
  | { kind: "service"; name: string; label: string; distance: number }
  | { kind: "npc"; npcId: string; label: string; distance: number }
  | { kind: "mine"; label: string; distance: number };

function serviceName(state: GameState, index: number): string {
  const services = SETTLEMENTS[state.location].services;
  return services[index % services.length] ?? `residence ${index + 1}`;
}

function nearestInteraction(state: GameState, position: { x: number; y: number }): Interaction | null {
  const candidates: Interaction[] = [];
  if (state.location === "lantern-acre") {
    for (const placed of state.buildings) {
      const def = BUILDINGS[placed.buildingId];
      const doorX = placed.x + def.width / 2;
      const doorY = placed.y + def.height;
      const distance = Math.hypot(position.x - doorX, position.y - doorY);
      if (distance <= 3.1) candidates.push({ kind: "building", uid: placed.uid, label: `Enter ${def.name}`, distance });
    }
    const mineDistance = Math.hypot(position.x - 38, position.y - 24);
    if (mineDistance <= 4.2) candidates.push({ kind: "mine", label: "Enter Rootways", distance: mineDistance });
  } else {
    for (const building of townBuildings(state.location)) {
      const doorX = building.x + building.width / 2;
      const doorY = building.y + building.height;
      const distance = Math.hypot(position.x - doorX, position.y - doorY);
      const name = serviceName(state, building.index);
      if (distance <= 3.2) candidates.push({ kind: "service", name, label: `Enter ${name}`, distance });
    }
    for (const npc of npcsAtLocation(state)) {
      const schedule = currentNpcSchedule(npc, state.minute);
      const distance = Math.hypot(schedule.x - position.x, schedule.y - position.y);
      if (distance <= 3) candidates.push({ kind: "npc", npcId: npc.id, label: `Talk to ${npc.name}`, distance });
    }
  }
  return candidates.sort((a, b) => a.distance - b.distance)[0] ?? null;
}

function drawFarm(ctx: CanvasRenderingContext2D, state: GameState) {
  for (const plot of state.farm) {
    const x = (8 + plot.x) * TILE; const y = (5 + plot.y) * TILE;
    ctx.fillStyle = plot.tilled ? "#634b35" : "rgba(80,105,56,.25)"; ctx.fillRect(x + 1, y + 1, TILE - 2, TILE - 2);
    if (plot.watered) { ctx.fillStyle = "rgba(77,133,148,.42)"; ctx.fillRect(x + 3, y + 3, TILE - 6, TILE - 6); }
    if (plot.cropId) {
      const crop = CROP_BY_ID[plot.cropId];
      const stage = Math.max(.15, Math.min(1, plot.growth / crop.growthDays));
      drawCropSprite(ctx, plot.cropId, x, y, stage, plot.ready, plot.watered, TILE);
    }
    if (state.settings.showGrid) { ctx.strokeStyle = "rgba(232,220,178,.12)"; ctx.strokeRect(x, y, TILE, TILE); }
  }
}

function drawTown(ctx: CanvasRenderingContext2D, state: GameState) {
  const settlement = SETTLEMENTS[state.location];
  const [primary, accent, dark, light] = settlement.palette;
  drawSettlementGround(ctx, state.location, MAP_W * TILE, MAP_H * TILE, TILE);
  ctx.fillStyle = dark; ctx.fillRect(0, 17 * TILE, MAP_W * TILE, 6 * TILE);
  ctx.fillStyle = light; ctx.fillRect(0, 18 * TILE, MAP_W * TILE, 4 * TILE);
  for (const building of townBuildings(state.location)) {
    const service = serviceName(state, building.index);
    drawBuildingExterior(ctx, `service:${service}`, building.x * TILE, building.y * TILE, building.width * TILE, building.height * TILE, [primary, accent, dark], service, 1, state.location);
  }
  ctx.fillStyle = accent; ctx.fillRect(18 * TILE, 14 * TILE, 6 * TILE, 6 * TILE);
  ctx.fillStyle = dark; ctx.fillRect(19 * TILE, 15 * TILE, 4 * TILE, 4 * TILE);
  const npcs = npcsAtLocation(state);
  for (const npc of npcs) {
    const schedule = currentNpcSchedule(npc, state.minute);
    const nx = schedule.x * TILE; const ny = schedule.y * TILE;
    ctx.save(); ctx.translate(nx, ny + 17); ctx.scale(1.34, 1.34); ctx.translate(-nx, -ny - 17);
    drawNpcWorld(ctx, npc.id, nx, ny, state.minute); ctx.restore();
  }
}

function drawHomestead(ctx: CanvasRenderingContext2D, state: GameState) {
  drawSettlementGround(ctx, "lantern-acre", MAP_W * TILE, MAP_H * TILE, TILE);
  ctx.fillStyle = "#86704e"; ctx.fillRect(0, 17 * TILE, MAP_W * TILE, 2 * TILE);
  drawFarm(ctx, state);
  for (const placed of state.buildings) {
    const def = BUILDINGS[placed.buildingId];
    drawBuildingExterior(ctx, placed.buildingId, placed.x * TILE, placed.y * TILE, def.width * TILE, def.height * TILE, def.palette, def.name, placed.tier, "lantern-acre");
  }
  state.animals.forEach((owned, index) => {
    const animal = ANIMALS[owned.animalId];
    const shelter = state.buildings.find((placed) => BUILDINGS[placed.buildingId].tags.includes(animal.buildingTag));
    const shelterDef = shelter ? BUILDINGS[shelter.buildingId] : undefined;
    const baseX = shelter && shelterDef ? shelter.x + shelterDef.width / 2 : 5;
    const baseY = shelter && shelterDef ? shelter.y + shelterDef.height + 1 : 14;
    const ax = (baseX + (index % 3 - 1) * 1.25) * TILE; const ay = (baseY + Math.floor(index / 3) * 1.15) * TILE;
    ctx.save(); ctx.translate(ax, ay + 10); ctx.scale(1.32, 1.32); ctx.translate(-ax, -ay - 10);
    drawAnimalWorld(ctx, owned.animalId, ax, ay, state.minute / 90 + index); ctx.restore();
  });
  drawTree(ctx, 25 * TILE, 7 * TILE, 0, state.season); drawTree(ctx, 30 * TILE, 10 * TILE, 1, state.season); drawTree(ctx, 34 * TILE, 20 * TILE, 2, state.season);
  ctx.fillStyle = "#1e2023"; ctx.fillRect(36 * TILE, 22 * TILE, 4 * TILE, 4 * TILE); ctx.fillStyle = "#755a3d"; ctx.fillRect(36.4 * TILE, 22.4 * TILE, 3.2 * TILE, .4 * TILE);
  ctx.fillStyle = "#d8c57f"; ctx.font = "bold 10px sans-serif"; ctx.textAlign = "center"; ctx.fillText("ROOTWAYS", 38 * TILE, 21.5 * TILE);
}

export function WorldCanvas({ state, onMove, onFarm, onTalk, onEnterMine, onEnterBuilding, onEnterService }: {
  state: GameState;
  onMove: (x: number, y: number, direction: GameState["position"]["direction"], moving: boolean, distance: number) => void;
  onFarm: (plotId: string, mode?: ToolMode) => void;
  onTalk: (npcId: string) => void;
  onEnterMine: () => void;
  onEnterBuilding: (buildingUid: string) => void;
  onEnterService: (name: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(state);
  const positionRef = useRef({ x: state.position.x, y: state.position.y, direction: state.position.direction, frame: 0 });
  const keys = useRef(new Set<string>());
  const joystick = useRef({ x: 0, y: 0 });
  const lastCommit = useRef(0);
  const touchCapable = useTouchCapability();
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { positionRef.current = { x: state.position.x, y: state.position.y, direction: state.position.direction, frame: 0 }; }, [state.location]);

  const interact = useCallback(() => {
    const current = stateRef.current;
    const target = nearestInteraction(current, positionRef.current);
    if (!target) return;
    if (target.kind === "building") onEnterBuilding(target.uid);
    if (target.kind === "service") onEnterService(target.name);
    if (target.kind === "npc") onTalk(target.npcId);
    if (target.kind === "mine") onEnterMine();
  }, [onEnterBuilding, onEnterMine, onEnterService, onTalk]);

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      keys.current.add(event.key.toLowerCase());
      if (["arrowup", "arrowdown", "arrowleft", "arrowright", " "].includes(event.key.toLowerCase())) event.preventDefault();
      if (event.key.toLowerCase() === "e") interact();
    };
    const up = (event: KeyboardEvent) => keys.current.delete(event.key.toLowerCase());
    window.addEventListener("keydown", down); window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [interact]);

  useEffect(() => {
    let animation = 0; let last = performance.now();
    const loop = (now: number) => {
      const canvas = canvasRef.current; if (!canvas) return;
      const ctx = canvas.getContext("2d"); if (!ctx) return;
      const dt = Math.min(.05, (now - last) / 1000); last = now;
      const current = stateRef.current; const pos = positionRef.current;
      const left = keys.current.has("a") || keys.current.has("arrowleft"); const right = keys.current.has("d") || keys.current.has("arrowright");
      const up = keys.current.has("w") || keys.current.has("arrowup"); const down = keys.current.has("s") || keys.current.has("arrowdown");
      let dx = (right ? 1 : 0) - (left ? 1 : 0) + joystick.current.x;
      let dy = (down ? 1 : 0) - (up ? 1 : 0) + joystick.current.y;
      const length = Math.hypot(dx, dy);
      if (length > 1) { dx /= length; dy /= length; }
      const moving = length > .08; const speed = 4.5 * current.stats.speed;
      pos.x = Math.max(.6, Math.min(MAP_W - .6, pos.x + dx * speed * dt)); pos.y = Math.max(.9, Math.min(MAP_H - .6, pos.y + dy * speed * dt));
      if (moving) { pos.direction = Math.abs(dx) > Math.abs(dy) ? dx > 0 ? "right" : "left" : dy > 0 ? "down" : "up"; pos.frame += dt * 8; }
      if (now - lastCommit.current > 120) { onMove(pos.x, pos.y, pos.direction, moving, moving ? speed * .12 : 0); lastCommit.current = now; }

      const cssW = canvas.clientWidth; const cssH = canvas.clientHeight; const ratio = Math.min(2, window.devicePixelRatio || 1);
      if (canvas.width !== Math.round(cssW * ratio) || canvas.height !== Math.round(cssH * ratio)) { canvas.width = Math.round(cssW * ratio); canvas.height = Math.round(cssH * ratio); }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0); ctx.imageSmoothingEnabled = false;
      const viewW = cssW; const viewH = cssH;
      const cameraX = Math.max(0, Math.min(MAP_W * TILE - viewW, pos.x * TILE - viewW / 2));
      const cameraY = Math.max(0, Math.min(MAP_H * TILE - viewH, pos.y * TILE - viewH / 2));
      ctx.clearRect(0, 0, viewW, viewH); ctx.save(); ctx.translate(-cameraX, -cameraY);
      if (current.location === "lantern-acre") drawHomestead(ctx, current); else drawTown(ctx, current);
      const activePet = current.pets.find((pet) => pet.uid === current.activePetUid);
      if (activePet) {
        const petX = pos.x * TILE - 25; const petY = pos.y * TILE + 15;
        ctx.save(); ctx.translate(petX, petY + 8); ctx.scale(1.5, 1.5); ctx.translate(-petX, -petY - 8);
        drawPetWorld(ctx, activePet.petId, petX, petY, pos.frame); ctx.restore();
      }
      drawPlayerWorld(ctx, current, pos.x * TILE, pos.y * TILE, pos.frame);
      ctx.restore();
      const darkness = current.minute < 420 || current.minute > 1200 ? .32 : current.minute > 1080 ? (current.minute - 1080) / 600 : 0;
      if (darkness > 0) { ctx.fillStyle = `rgba(18,24,48,${darkness})`; ctx.fillRect(0, 0, viewW, viewH); }
      if (["rain", "storm"].includes(current.weather)) { ctx.strokeStyle = "rgba(170,210,224,.45)"; ctx.lineWidth = 1; for (let i = 0; i < 80; i += 1) { const rx = (i * 83 + now / 8) % viewW; const ry = (i * 47 + now / 5) % viewH; ctx.beginPath(); ctx.moveTo(rx, ry); ctx.lineTo(rx - 5, ry + 12); ctx.stroke(); } }
      animation = requestAnimationFrame(loop);
    };
    animation = requestAnimationFrame(loop); return () => cancelAnimationFrame(animation);
  }, [onMove]);

  const click = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect(); const pos = positionRef.current;
    const cameraX = Math.max(0, Math.min(MAP_W * TILE - rect.width, pos.x * TILE - rect.width / 2)); const cameraY = Math.max(0, Math.min(MAP_H * TILE - rect.height, pos.y * TILE - rect.height / 2));
    const worldX = (event.clientX - rect.left + cameraX) / TILE; const worldY = (event.clientY - rect.top + cameraY) / TILE;
    if (state.location === "lantern-acre") {
      const building = state.buildings.find((placed) => { const def = BUILDINGS[placed.buildingId]; return worldX >= placed.x && worldX <= placed.x + def.width && worldY >= placed.y && worldY <= placed.y + def.height; });
      if (building) {
        const def = BUILDINGS[building.buildingId];
        if (Math.hypot(pos.x - (building.x + def.width / 2), pos.y - (building.y + def.height)) <= 3.4) onEnterBuilding(building.uid);
        return;
      }
      const plotX = Math.floor(worldX - 8); const plotY = Math.floor(worldY - 5);
      if (plotX >= 0 && plotX < 12 && plotY >= 0 && plotY < 10) onFarm(`plot-${plotX}-${plotY}`);
      return;
    }
    const townBuilding = townBuildings(state.location).find((building) => worldX >= building.x && worldX <= building.x + building.width && worldY >= building.y && worldY <= building.y + building.height);
    if (townBuilding && Math.hypot(pos.x - (townBuilding.x + townBuilding.width / 2), pos.y - (townBuilding.y + townBuilding.height)) <= 3.5) onEnterService(serviceName(state, townBuilding.index));
  };

  const interaction = nearestInteraction(state, state.position);
  return <div className={`canvas-wrap world-canvas-wrap ${touchCapable ? "touch-capable" : ""}`}>
    <canvas ref={canvasRef} onClick={click} aria-label={`${SETTLEMENTS[state.location].name} game world`} />
    <div className="world-help"><b>WASD / arrows</b> move · <b>E</b> enter/talk · tap nearby doors</div>
    {interaction && <button className="interaction-prompt" onClick={interact}><span>E</span>{interaction.label}</button>}
    {touchCapable && <div className="world-touch-controls">
      <VirtualJoystick onVector={(x, y) => { joystick.current = { x, y }; }} />
      <button className="touch-interact" disabled={!interaction} onClick={interact}><span>E</span><b>{interaction?.label ?? "Move near something"}</b></button>
    </div>}
  </div>;
}
